import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Search, Zap, AlertCircle, CheckCircle, Mail, Calendar, ChevronRight, Share2 } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { BreakthroughReportPanel } from '../../components/ai/BreakthroughReportPanel';
import type { AssessmentResult } from '../../types/results';

interface RespondentWithResult {
  userId: string;
  name: string;
  email: string;
  completedAt: Date;
  result: AssessmentResult;
  isShared?: boolean;
}

export function ManualAIAnalysis() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondents, setRespondents] = useState<RespondentWithResult[]>([]);
  const [selectedRespondent, setSelectedRespondent] = useState<RespondentWithResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        if (!aiAccess) {
          setHasAccess(false);
          setIsLoading(false);
          return;
        }

        setHasAccess(true);

        const { data: coach, error: coachError } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (coachError || !coach) {
          navigate('/login');
          return;
        }

        const { data: respondentsData, error: respondentsError } = await supabase
          .from('respondents')
          .select('user_id, users!respondents_user_id_fkey(full_name, email)')
          .eq('coach_id', coach.id);

        if (respondentsError) throw respondentsError;

        const respondentsWithResults: RespondentWithResult[] = [];

        for (const respondent of respondentsData || []) {
          const { data: resultData, error: resultError } = await supabase
            .from('results')
            .select('*')
            .eq('user_id', respondent.user_id)
            .order('completed_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (resultError) throw resultError;

          if (resultData) {
            const userRecord = respondent.users as { full_name?: string; email?: string } | null;
            respondentsWithResults.push({
              userId: respondent.user_id,
              name: userRecord?.full_name || userRecord?.email || 'Unknown',
              email: userRecord?.email || '',
              completedAt: new Date(resultData.completed_at || Date.now()),
              result: {
                id: resultData.id,
                userId: resultData.user_id,
                coachId: resultData.coach_id || undefined,
                partnerId: resultData.partner_id || undefined,
                scores: resultData.scores as any,
                harmonicScores: resultData.harmonic_scores as any,
                completedAt: new Date(resultData.completed_at || Date.now()),
                aiAnalysis: resultData.ai_analysis || undefined,
                coachAiAnalysis: resultData.coach_ai_analysis || undefined,
                breakthroughAnalysis: resultData.breakthrough_analysis || undefined,
                version: resultData.version || 1,
                originalResultId: resultData.original_result_id || undefined
              }
            });
          }
        }

        // Also load respondents who shared their results with this coach
        const { data: sharedData, error: sharedError } = await supabase
          .rpc('fetch_received_shares', {
            p_recipient_type: 'coach',
            p_recipient_id: coach.id,
          });

        if (sharedError) throw sharedError;

        const ownUserIds = new Set(respondentsWithResults.map(r => r.userId));

        for (const share of sharedData || []) {
          if (ownUserIds.has(share.result_user_id)) continue;

          respondentsWithResults.push({
            userId: share.result_user_id,
            name: share.sharer_name || 'Respondent',
            email: share.sharer_email || '',
            completedAt: new Date(share.result_completed_at || Date.now()),
            isShared: true,
            result: {
              id: share.result_id,
              userId: share.result_user_id,
              scores: share.result_scores as any,
              harmonicScores: share.result_harmonic_scores as any,
              completedAt: new Date(share.result_completed_at || Date.now()),
              coachAiAnalysis: share.result_coach_ai_analysis || undefined,
              breakthroughAnalysis: share.result_breakthrough_analysis || undefined,
              version: 1,
            }
          });
        }

        respondentsWithResults.sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
        setRespondents(respondentsWithResults);
      } catch (loadError) {
        console.error('Error loading breakthrough data:', loadError);
        setError('We could not load the breakthrough workspace. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleAnalysisUpdate = (analysis: string) => {
    setSelectedRespondent(current => {
      if (!current) return current;
      return {
        ...current,
        result: { ...current.result, breakthroughAnalysis: analysis },
      };
    });

    setRespondents(current => current.map(respondent =>
      respondent.userId === selectedRespondent?.userId
        ? { ...respondent, result: { ...respondent.result, breakthroughAnalysis: analysis } }
        : respondent
    ));
  };

  const handleBack = () => {
    setSelectedRespondent(null);
  };

  const filteredRespondents = respondents.filter(respondent =>
    respondent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    respondent.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading breakthrough workspace..." />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="freud-heading-xl text-chocolate-900 mb-4">Access Restricted</h1>
            <p className="text-chocolate-500 mb-6">
              The NLP OS Breakthrough Report is available to coaches with Advanced or Partner tier access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard view for selected respondent
  if (selectedRespondent) {
    return (
      <BreakthroughReportPanel
        key={selectedRespondent.userId}
        analysis={selectedRespondent.result.breakthroughAnalysis}
        scores={selectedRespondent.result.scores}
        harmonicScores={selectedRespondent.result.harmonicScores}
        resultId={selectedRespondent.result.id}
        respondentName={selectedRespondent.name}
        respondentEmail={selectedRespondent.email}
        completedAt={selectedRespondent.completedAt}
        onAnalysisUpdate={handleAnalysisUpdate}
        onBack={handleBack}
      />
    );
  }

  // Respondent list view
  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-coral-100 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-coral-600" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">NLP OS Breakthrough Report</h1>
              <p className="text-sm text-chocolate-500">Select a respondent to generate or view their breakthrough report</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
            <input
              type="text"
              placeholder="Search respondents..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="freud-input pl-10"
            />
          </div>
        </div>

        {/* Respondent Table */}
        <div className="freud-card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-cream-200 bg-cream-50/50">
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-chocolate-400">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-chocolate-400">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-chocolate-400">Assessed</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-chocolate-400">Report Status</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-chocolate-400"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredRespondents.map((respondent) => (
                  <tr
                    key={respondent.userId}
                    className="hover:bg-cream-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedRespondent(respondent)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-chocolate-900">{respondent.name}</span>
                        {respondent.isShared && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sage-50 text-sage-600 text-[10px] font-bold uppercase tracking-wide">
                            <Share2 className="w-3 h-3" />
                            Shared
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Mail className="w-4 h-4 text-chocolate-400" />
                        {respondent.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-chocolate-600">
                        <Calendar className="w-4 h-4 text-chocolate-400" />
                        {respondent.completedAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {respondent.result.breakthroughAnalysis ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Report saved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warm-100 text-warm-700 rounded-full text-sm">
                          <Brain className="w-3 h-3" />
                          Not generated
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5 text-coral-600 font-medium text-sm">
                        Open Report
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRespondents.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No respondents with completed assessments found
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
